import type { Dispatch, UnknownAction } from 'redux';
import { Platform } from 'react-native';
import {
  completeActivationTask,
  isLocationScopedTask,
  type ActivationCompletionSource,
  type ActivationLocationType,
  type ActivationTaskId,
} from '../redux/ownerActivationSlice';
import ownerActivationAnalytics from './ownerActivationAnalytics';
import { trackTaskCompletionOnce } from './ownerActivationInstrumentation';
import type { OwnerActivationAnalyticsPlatform } from './ownerActivationAnalyticsTypes';

/**
 * Session-local guard so duplicate domain deliveries don't re-emit analytics.
 * Location-scoped tasks are keyed by `${locationId}:${taskId}`.
 * Global visit_test_location uses just the taskId.
 */
const trackedTaskCompletions = new Set<string>();

/** Build a session-dedupe key for a task completion. */
const buildCompletionKey = (
  taskId: ActivationTaskId,
  locationId: string | null,
): string => {
  if (taskId === 'visit_test_location') {
    return taskId;
  }
  return locationId ? `${locationId}:${taskId}` : taskId;
};

/**
 * Typed boundary between real feature successes and Home activation tasks.
 * UI components MUST NOT reproduce milestone rules; they dispatch these
 * domain events instead.
 *
 * See specs/001-new-owner-setup-wizard/contracts/activation-events.md.
 */

export type OwnerActivationDomainEvent =
  | {
      type: 'PRODUCT_CREATED';
      occurredAt: string;
      source:
        | 'owner_setup'
        | 'activation_tracker'
        | 'new_product'
        | 'backoffice_product';
      locationId: string;
      locationType: ActivationLocationType;
      savedProductIds: string[];
    }
  | {
      type: 'SALE_COMPLETED';
      occurredAt: string;
      source: 'checkout';
      locationId: string | null;
      locationType: ActivationLocationType;
    }
  | {
      type: 'STAFF_CREATED';
      occurredAt: string;
      source: 'backoffice_add_employee';
      createdEmployeeId: string;
      ownerEmployeeId: string;
      locationId: string;
      locationType: ActivationLocationType;
    }
  | {
      type: 'BACKOFFICE_VIEWED';
      occurredAt: string;
      source: 'backoffice_focus';
      routeName: string;
      locationId: string;
      locationType: ActivationLocationType;
    }
  | {
      type: 'LOCATION_SWITCHED';
      occurredAt: string;
      source: 'location_picker';
      locationId: string;
      locationType: ActivationLocationType;
    };

export type ActivationTaskCompletion = {
  taskId: ActivationTaskId;
  completedAt: string;
  completionSource: ActivationCompletionSource;
  locationId: string | null;
  locationType: ActivationLocationType | null;
};

const hasIsoTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

/**
 * Map a domain event to an activation task completion, or null when the
 * event does not constitute valid completion evidence.
 */
export function resolveActivationCompletion(
  event: OwnerActivationDomainEvent,
): ActivationTaskCompletion | null {
  if (!event || !hasIsoTimestamp(event.occurredAt)) return null;

  switch (event.type) {
    case 'PRODUCT_CREATED': {
      if (event.locationType !== 'real') return null;
      if (!event.locationId) return null;
      if (
        !Array.isArray(event.savedProductIds) ||
        event.savedProductIds.filter(id => typeof id === 'string' && id).length ===
          0
      ) {
        return null;
      }
      return {
        taskId: 'add_product',
        completedAt: event.occurredAt,
        completionSource:
          event.source === 'owner_setup'
            ? 'wizard_product_create'
            : 'feature_success',
        locationId: event.locationId,
        locationType: 'real',
      };
    }

    case 'SALE_COMPLETED': {
      // A terminal test-location sale may be practice analytics but never
      // completes First sale.
      if (event.locationType !== 'real') return null;
      // Require non-null locationId for location-scoped task completion.
      if (!event.locationId) return null;
      return {
        taskId: 'first_sale',
        completedAt: event.occurredAt,
        completionSource: 'feature_success',
        locationId: event.locationId,
        locationType: 'real',
      };
    }

    case 'STAFF_CREATED': {
      const created = event.createdEmployeeId;
      if (typeof created !== 'string' || created.length === 0) return null;
      if (created === event.ownerEmployeeId) return null;
      // Require real locationId for location-scoped task completion.
      if (event.locationType !== 'real') return null;
      if (!event.locationId) return null;
      return {
        taskId: 'add_staff',
        completedAt: event.occurredAt,
        completionSource: 'feature_success',
        locationId: event.locationId,
        locationType: 'real',
      };
    }

    case 'BACKOFFICE_VIEWED': {
      if (typeof event.routeName !== 'string' || event.routeName.length === 0) {
        return null;
      }
      // Require real locationId for location-scoped task completion.
      if (event.locationType !== 'real') return null;
      if (!event.locationId) return null;
      return {
        taskId: 'explore_backoffice',
        completedAt: event.occurredAt,
        completionSource: 'feature_success',
        locationId: event.locationId,
        locationType: 'real',
      };
    }

    case 'LOCATION_SWITCHED': {
      if (event.locationType !== 'test') return null;
      if (!event.locationId) return null;
      return {
        taskId: 'visit_test_location',
        completedAt: event.occurredAt,
        completionSource: 'feature_success',
        locationId: event.locationId,
        locationType: 'test',
      };
    }

    default:
      return null;
  }
}

/**
 * Non-throwing dispatcher: resolves the event and records local completion.
 * Milestone processing must never throw into checkout/product/employee/
 * Back Office/location success paths.
 *
 * Returns the resolved completion (or null) so callers can emit analytics
 * separately without re-deriving mapping rules.
 */
export function dispatchOwnerActivationEvent(
  dispatch: Dispatch<UnknownAction>,
  event: OwnerActivationDomainEvent,
): ActivationTaskCompletion | null {
  try {
    const completion = resolveActivationCompletion(event);
    if (!completion) return null;
    const completionKey = buildCompletionKey(
      completion.taskId,
      completion.locationId,
    );
    const before = {
      completedAt: trackedTaskCompletions.has(completionKey)
        ? completion.completedAt
        : null,
    };
    dispatch(completeActivationTask(completion));
    try {
      const platform: OwnerActivationAnalyticsPlatform =
        Platform.OS === 'ios'
          ? 'ios'
          : Platform.OS === 'android'
            ? 'android'
            : 'web';
      trackTaskCompletionOnce(
        ownerActivationAnalytics,
        before,
        { completedAt: completion.completedAt },
        {
          onboarding_version: 1,
          platform,
          tracker_phase: 'active',
          completed_task_count: 0,
          wizard_product_created: false,
          task_id: completion.taskId,
          completion_source: completion.completionSource,
        },
      );
      trackedTaskCompletions.add(completionKey);
    } catch {
      // Analytics must never break the feature success path.
    }
    return completion;
  } catch (error) {
    // Log only normalized identifiers; never rethrow into the feature flow.
    try {
      console.warn(
        '[ownerActivation] event processing failed',
        (event as { type?: string })?.type ?? 'unknown',
      );
    } catch {
      // ignore
    }
    return null;
  }
}
